import { type ReactElement } from 'react';
import { Check } from 'lucide-react';
import { ForesightLink } from '@/components/link/foresight-link';
import { focusVisibleOutline } from '@/lib/focus-styles';
import { cn } from '@/lib/utils';

/**
 * PLP subcategory quick filter “Tag / Toggle” (Figma: Library → TagToggle, node 28:840).
 * Off: sand-95 + neutral-10. On: sand-20 + neutral-white + 16px check.
 */
const tagClassName = (isSelected: boolean) =>
    cn(
        'body-copy-m inline-flex h-10 min-h-10 w-max shrink-0 cursor-pointer items-center justify-center gap-1 rounded-sm transition-colors',
        `whitespace-nowrap tracking-[0.14px] ${focusVisibleOutline}`,
        isSelected ? 'bg-sand-20 pl-2 pr-4 text-neutral-white' : 'bg-sand-95 px-4 text-neutral-10 hover:bg-brown-90'
    );

export type EatalyPlpSubcategoryTagButtonProps = {
    label: string;
    isSelected: boolean;
    onPress: () => void;
    'aria-pressed': boolean;
};

export function EatalyPlpSubcategoryTagButton({
    label,
    isSelected,
    onPress,
    'aria-pressed': ariaPressed,
}: EatalyPlpSubcategoryTagButtonProps): ReactElement {
    return (
        <button type="button" className={tagClassName(isSelected)} onClick={onPress} aria-pressed={ariaPressed}>
            {isSelected ? (
                <Check className="size-4 shrink-0 text-neutral-white" strokeWidth={2.25} aria-hidden />
            ) : null}
            <span className="whitespace-nowrap">{label}</span>
        </button>
    );
}

export type EatalyPlpSubcategoryTagLinkProps = {
    label: string;
    isSelected: boolean;
    to: string;
    /** Set when the document URL is this category (avoids `aria-current` on parent PLP + refine). */
    isCurrentPage: boolean;
    /** Fired on click before navigation (e.g. optimistic PLP header title). */
    onNavigate?: () => void;
};

export function EatalyPlpSubcategoryTagLink({
    label,
    isSelected,
    to,
    isCurrentPage,
    onNavigate,
}: EatalyPlpSubcategoryTagLinkProps): ReactElement {
    return (
        <ForesightLink
            to={to}
            hitSlop={8}
            name={`plp-subcategory:${to}`}
            className={tagClassName(isSelected)}
            aria-current={isCurrentPage ? 'page' : undefined}
            onClick={onNavigate}>
            {isSelected ? (
                <Check className="size-4 shrink-0 text-neutral-white" strokeWidth={2.25} aria-hidden />
            ) : null}
            <span className="whitespace-nowrap">{label}</span>
        </ForesightLink>
    );
}
